

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// CONTENT FILES 
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define a more specific media category type
export type MediaCategory = 'image' | 'video' | 'audio' | 'document' | 'spreadsheet' | 'presentation' | 'other'

// Unified ContentFile interface that handles both uploading and completed states
export interface ContentFile {
    id: string
    name: string
    size: number
    
    // Always present fields
    extension?: string
    mediaCategory?: MediaCategory
    mimeType?: string
    
    // Completed file fields (only present after upload)
    createdAt?: Date
    url?: string  
    blurDataURL?: string
    generatedBy?: 'user' | 'assistant'
    content?: ContentFileContent
    
    // Upload state (present during upload)
    uploadState?: {
        status: 'pending' | 'uploading' | 'complete' | 'error'
        progress: number  // 0-100
        error?: string
        file?: File  // For preview during upload, can create object URL
    }
}

export interface ContentFileContent {
    text?: string
}

// Legacy support - now just an alias to ContentFile
export type UploadingFile = ContentFile

// Type for components that need to work with files in any state
export type FileCardFile = ContentFile

// Helper functions to work with unified file type
export function isUploadingFile(file: ContentFile): boolean {
    return file.uploadState?.status === 'uploading' || file.uploadState?.status === 'pending'
}

export function isCompletedFile(file: ContentFile): boolean {
    return file.uploadState?.status === 'complete' || (!file.uploadState && !!file.url)
}

export function hasUploadError(file: ContentFile): boolean {
    return file.uploadState?.status === 'error'
}

export function getFilePreviewUrl(file: ContentFile): string | undefined {
    // For completed files, use the stored URL
    if (file.url) {
        return file.url
    }
    
    // For uploading files with File object, create object URL for preview
    if (file.uploadState?.file && file.mediaCategory === 'image') {
        return URL.createObjectURL(file.uploadState.file)
    }
    
    return undefined
}

// Create uploading file from File object
export function createUploadingFile(file: File): ContentFile {
    const extension = file.name.split('.').pop()?.toLowerCase() || ''
    const mimeType = file.type || getMimeTypeFromExtension(extension)
    const mediaCategory = getMediaCategoryFromMimeType(mimeType)
    
    return {
        id: Math.random().toString(36).substring(2) + Date.now().toString(36),
        name: file.name,
        size: file.size,
        extension,
        mimeType,
        mediaCategory,
        uploadState: {
            status: 'pending',
            progress: 0,
            file
        }
    }
}

// Convert uploaded file response to completed ContentFile
export function createCompletedFile(uploadResponse: any): ContentFile {
    return {
        id: uploadResponse.id,
        name: uploadResponse.name,
        size: uploadResponse.size,
        createdAt: new Date(uploadResponse.createdAt),
        url: uploadResponse.url,
        blurDataURL: uploadResponse.blurDataURL || '',
        extension: uploadResponse.extension,
        mediaCategory: uploadResponse.mediaCategory,
        mimeType: uploadResponse.mimeType,
        generatedBy: uploadResponse.generatedBy || 'user',
        content: uploadResponse.content,
        uploadState: {
            status: 'complete',
            progress: 100
        }
    }
}

// Helper functions to work with file types
export function getMimeTypeFromExtension(extension: string): string {
    const mimeTypeMap: Record<string, string> = {
        // Images
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'webp': 'image/webp',
        'svg': 'image/svg+xml',
        'bmp': 'image/bmp',
        'tiff': 'image/tiff',
        'tif': 'image/tiff',
        
        // Videos
        'mp4': 'video/mp4',
        'webm': 'video/webm',
        'avi': 'video/x-msvideo',
        'mov': 'video/quicktime',
        'wmv': 'video/x-ms-wmv',
        'mkv': 'video/x-matroska',
        
        // Audio
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'ogg': 'audio/ogg',
        'flac': 'audio/flac',
        'aac': 'audio/aac',
        'm4a': 'audio/mp4',
        
        // Documents
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'rtf': 'application/rtf',
        'txt': 'text/plain',
        'md': 'text/markdown',
        'html': 'text/html',
        'htm': 'text/html',
        'xml': 'text/xml',
        'odt': 'application/vnd.oasis.opendocument.text',
        
        // Spreadsheets
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'csv': 'text/csv',
        'ods': 'application/vnd.oasis.opendocument.spreadsheet',
        'numbers': 'application/vnd.apple.numbers',
        
        // Presentations
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'odp': 'application/vnd.oasis.opendocument.presentation',
        'key': 'application/vnd.apple.keynote',
    }
    
    return mimeTypeMap[extension.toLowerCase()] || 'application/octet-stream'
}

export function getMediaCategoryFromMimeType(mimeType: string): MediaCategory {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'
    if (mimeType.startsWith('audio/')) return 'audio'
    
    // Document types
    const documentTypes = [
        'application/pdf', 
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/rtf',
        'text/plain',
        'text/markdown',
        'text/html',
        'text/xml',
        'application/vnd.oasis.opendocument.text'
    ]
    if (documentTypes.includes(mimeType)) return 'document'
    
    // Spreadsheet types
    const spreadsheetTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        'application/vnd.oasis.opendocument.spreadsheet',
        'application/vnd.apple.numbers'
    ]
    if (spreadsheetTypes.includes(mimeType)) return 'spreadsheet'
    
    // Presentation types
    const presentationTypes = [
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.oasis.opendocument.presentation',
        'application/vnd.apple.keynote'
    ]
    if (presentationTypes.includes(mimeType)) return 'presentation'
    
    return 'other'
}
